import { useEffect, useMemo } from "react";
import { useSetRecoilState } from "recoil";
import { AlertState } from "states";
import { axiosPrivateInstance } from "../axios";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";

export default function useAxiosPrivate() {
  const { t } = useTranslation();
  const {
    accessToken,
    setAccessToken,
    csrftoken,
    user,
    setCSRFToken,
    setStatusAuth,
    setUser,
  } = useAuth();
  const refresh = useRefreshToken();
  const setAlert = useSetRecoilState(AlertState);
  const location = useLocation();

  const axiosPrivate = useMemo(() => axiosPrivateInstance, []);

  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${accessToken}`;
          config.headers["X-CSRFToken"] = csrftoken;
          config.headers["Content-Type"] = "application/json";
          config.headers["Accept"] = "application/json";
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;

        if (error.response && error.response.status === 500) {
          setAlert({
            alert: true,
            message: t("error_500"),
            type: "error",
          });
          return Promise.reject(error);
        }

        if (prevRequest?.url.includes("logout")) {
          return Promise.reject(error);
        }

        if (
          (error?.response?.status === 403 ||
            error?.response?.status === 401) &&
          !prevRequest?.sent
        ) {
          prevRequest.sent = true;
          try {
            if (location.pathname === "/sign-in") {
              return Promise.reject(error);
            }

            const refreshResult = await refresh();
            if (!refreshResult) {
              throw new Error("Failed to refresh token");
            }

            const { accessToken: newAccessToken, csrfToken: newCSRFToken } =
              refreshResult;

            setAccessToken(newAccessToken);
            setCSRFToken(newCSRFToken);

            const userResponse = await axiosPrivate.get("user", {
              headers: {
                Authorization: `Bearer ${newAccessToken}`,
                "X-CSRFToken": newCSRFToken,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              withCredentials: true,
            });

            if (userResponse.status === 200) {
              setUser(userResponse.data);
              const expirationDate = new Date();
              expirationDate.setDate(expirationDate.getDate() + 1);
              Cookies.set("user", JSON.stringify(userResponse.data), {
                expires: expirationDate,
                secure: false,
                sameSite: "Lax",
              });
            }

            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            prevRequest.headers["X-CSRFToken"] = newCSRFToken;
            return axiosPrivate(prevRequest);
          } catch (refreshError) {
            console.error("Refresh token error:", refreshError);
            if (location.pathname !== "/sign-in") {
              window.location.href = "/sign-in";
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [
    accessToken,
    user,
    refresh,
    setAccessToken,
    csrftoken,
    setAlert,
    t,
    location,
    setCSRFToken,
    setStatusAuth,
    setUser,
  ]);

  return axiosPrivate;
}
